import type { AlertRule, ApplicationWithStats, MessageTypeWithStats } from '@owl/shared';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { SeverityChip } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

const LABEL: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  fontWeight: 500,
  marginBottom: 8,
};

export function AlertRuleEditSheet({
  ctx,
  ruleId,
}: {
  ctx: MobileShellCtx;
  ruleId: string | null;
}) {
  const [rule, setRule] = useState<AlertRule | null>(null);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [mts, setMts] = useState<MessageTypeWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [a, m] = await Promise.all([
          api.listApplications(ctx.environment),
          api.listMessageTypes(),
        ]);
        if (cancelled) return;
        setApps(a.items);
        setMts(m.items);
        if (ruleId) {
          const { rule: r } = await api.getAlertRule(ruleId);
          if (cancelled) return;
          setRule(r);
        }
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ruleId, ctx.environment]);

  const scopeApps = (): string => {
    if (!rule) return '—';
    if (rule.scopeApplications === 'all') return 'Todas las aplicaciones';
    const ids = rule.scopeApplications;
    const names = ids.map((id) => apps.find((a) => a.id === id)?.name ?? id);
    return names.length > 0 ? names.join(', ') : '—';
  };

  const scopeMts = (): string => {
    if (!rule) return '—';
    if (rule.scopeMessageTypes === 'all') return 'Todos los tipos';
    const ids = rule.scopeMessageTypes;
    const names = ids.map((id) => mts.find((m) => m.id === id)?.name ?? id);
    return names.length > 0 ? names.join(', ') : '—';
  };

  return (
    <BottomSheet title="Configuración de la regla" onClose={ctx.closeSheet}>
      {err && <div className="m-error-banner">{err}</div>}
      {loading && !err && <div className="m-loading">Cargando…</div>}
      {!loading && !rule && !err && (
        <div className="m-empty">Selecciona una regla para consultar su configuración.</div>
      )}
      {rule && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <SeverityChip severity={rule.severity} />
            <span
              className="mono"
              style={{
                marginLeft: 'auto',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: rule.status === 'active' ? 'var(--ok)' : 'var(--fg-4)',
              }}
            >
              {rule.status}
            </span>
          </div>

          <h3
            style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 500, letterSpacing: '-0.01em' }}
          >
            {rule.name}
          </h3>
          {rule.description && (
            <div
              style={{ color: 'var(--fg-3)', fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}
            >
              {rule.description}
            </div>
          )}

          <div style={LABEL}>Condición</div>
          <div className="rule-cond" style={{ marginBottom: 14 }}>
            {ruleCondition(rule)}
          </div>

          <div style={LABEL}>Scope</div>
          <div className="kv-block" style={{ marginBottom: 14 }}>
            <div className="kv-row">
              <span className="k">Aplicaciones</span>
              <span className="v">{scopeApps()}</span>
            </div>
            <div className="kv-row">
              <span className="k">Tipos</span>
              <span className="v">{scopeMts()}</span>
            </div>
          </div>

          <div style={LABEL}>Canales</div>
          <div className="kv-block" style={{ marginBottom: 14 }}>
            <div className="kv-row">
              <span className="k">Email</span>
              <span className="v">
                {rule.channels.email && rule.channels.email.length > 0
                  ? rule.channels.email.join(', ')
                  : '—'}
              </span>
            </div>
            <div className="kv-row">
              <span className="k">Discord</span>
              <span className="v">
                {typeof rule.channels.discord === 'string'
                  ? rule.channels.discord
                  : rule.channels.discord
                    ? 'sí'
                    : '—'}
              </span>
            </div>
            <div className="kv-row">
              <span className="k">Webhook</span>
              <span className="v">{rule.channels.webhook ? 'sí' : '—'}</span>
            </div>
            <div className="kv-row">
              <span className="k">Slack</span>
              <span className="v">{rule.channels.slack ? 'sí' : '—'}</span>
            </div>
          </div>

          <div style={LABEL}>Entrega</div>
          <div className="kv-block">
            <div className="kv-row">
              <span className="k">Cooldown</span>
              <span className="v">{rule.cooldownMinutes} min</span>
            </div>
            <div className="kv-row">
              <span className="k">Group similar</span>
              <span className="v">{rule.groupSimilar ? 'sí' : 'no'}</span>
            </div>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

function ruleCondition(rule: AlertRule): ReactNode {
  const c = rule.condition;
  switch (c.type) {
    case 'any_match':
      return <>Cualquier evento dentro del scope.</>;
    case 'threshold':
      return (
        <>
          Más de <span className="accent">{c.count}</span> en{' '}
          <span className="accent">{c.windowMinutes}m</span>.
        </>
      );
    case 'content_match':
      return (
        <>
          Match{' '}
          <span className="accent">
            {c.isRegex ? '/' : '"'}
            {c.pattern}
            {c.isRegex ? '/' : '"'}
          </span>{' '}
          en el mensaje.
        </>
      );
    case 'metadata_match':
      return (
        <>
          metadata.<span className="accent">{c.key}</span> {c.operator}{' '}
          <span className="accent">{c.value}</span>
        </>
      );
  }
}
